/* eslint-disable import/no-unresolved */
import React, { Dispatch, FunctionComponent, useCallback, useEffect, useState } from 'react';
import cn from 'classnames';
// @ts-ignore
import { EmblaCarouselType, EmblaOptionsType } from 'embla-carousel/vanilla';

import useWindowSize from 'hooks/useWindowSize';
import useMartyContext from 'hooks/useMartyContext';
import { getPDPTrackingPropsFormatted, PDP_GALLERY_CONFIG } from 'helpers/ProductUtils';
import { FeaturedImage, PDPCarouselRef, PDPFeaturedVideo } from 'types/product';
import { withErrorBoundary } from 'components/common/MartyErrorBoundary';
import MelodyVideoPlayer from 'components/common/melodyVideo/MelodyVideoPlayer';
import ImageZoom from 'components/common/zoom/ImageZoom';
import { track } from 'apis/amethyst';
import { evImageSwipe } from 'events/product';

import css from 'styles/components/productdetail/productGallery/productGalleryFeatured.scss';

interface ImageFeaturedProps {
  image: FeaturedImage;
  zoomIsActive: boolean;
  isSelected: boolean;
  showInstructions: boolean;
  setShowInstructions: Dispatch<React.SetStateAction<boolean>>;
  prependedAltText?: string;
  imageHasBadge?: boolean;
  renderDeselected?: boolean;
}

/**
 * PDP Assets gallery featured image container with a11y
 * @returns {FunctionComponent}
 */
const ImageFeatured: FunctionComponent<ImageFeaturedProps> = ({
  image,
  zoomIsActive,
  isSelected,
  showInstructions,
  setShowInstructions,
  prependedAltText,
  imageHasBadge,
  renderDeselected = false
}) => {
  if (!renderDeselected && !isSelected) {
    return null;
  }
  if (zoomIsActive) {
    return <ImageZoom image={image} showInstructions={showInstructions} setShowInstructions={setShowInstructions} />;
  }
  return (
    <picture className={cn({ [css.imageSizing]: imageHasBadge })}>
      {image.featured.webp && <source srcSet={`${image.featured.webp.src} 1x, ${image.featured.webp.retinaSrc}`} type="image/webp" />}
      <source srcSet={`${image.featured.src} 1x, ${image.featured.retinaSrc}`} type="image/jpeg" />
      <img
        alt={(prependedAltText && `${prependedAltText} - `) + image.alt}
        src={image.featured.src}
        srcSet={image.featured.retinaSrc}
        itemProp="image"
      />
    </picture>
  );
};

interface VideoFeaturedProps {
  video: PDPFeaturedVideo;
  isSelected: boolean;
}

/**
 * PDP Assets gallery featured video container with a11y
 * @returns {FunctionComponent}
 */
const VideoFeatured: FunctionComponent<VideoFeaturedProps> = ({ video, isSelected }) => <>{isSelected && <MelodyVideoPlayer {...video} />}</>;

interface Props {
  zoomIsActive: boolean;
  setZoomIsActive: Dispatch<React.SetStateAction<boolean>>;
  selectedAsset: number;
  styleId: string;
  productAssets: (FeaturedImage | PDPFeaturedVideo)[];
  setSelectedAsset: Dispatch<React.SetStateAction<number>>;
  productFeaturedRef: PDPCarouselRef;
  productFeaturedCarousel: EmblaCarouselType | undefined;
  productThumbnailsCarousel: EmblaCarouselType | undefined;
  prependedAltText?: string;
  imageHasBadge?: boolean;
  isHydraPartialImageGallery?: boolean;
}

/**
 * PDP Assets gallery featured container (featured image + featured video)
 * @returns {FunctionComponent}
 */
const ProductGalleryFeatured: FunctionComponent<Props> = ({
  zoomIsActive,
  setZoomIsActive,
  productAssets,
  selectedAsset,
  setSelectedAsset,
  styleId,
  productFeaturedRef,
  productFeaturedCarousel,
  productThumbnailsCarousel,
  prependedAltText,
  imageHasBadge,
  isHydraPartialImageGallery
}) => {
  const { testId } = useMartyContext();

  // Controls the zoom instructions message
  const [showInstructions, setShowInstructions] = useState(true);

  // We include `styleId` in the dependencies list of the useEffect call below
  // in order to re-render embla carousel when the product color is switched.
  // This hack is necessary because embla enters an invalid state if the number
  // of elements it needs to render changes.
  // === CAROUSEL CONFIG
  const { width: windowWidth } = useWindowSize();
  useEffect(() => {
    if (windowWidth && productFeaturedCarousel) {
      let options: EmblaOptionsType = {
        draggable: !zoomIsActive && windowWidth <= PDP_GALLERY_CONFIG.carouselThreshold,
        loop: true,
        selectedClass: '',
        speed: 100
      };

      if (isHydraPartialImageGallery) {
        options = {
          ...options,
          loop: false,
          dragFree: false,
          containScroll: 'trimSnaps'
        };
      }

      productFeaturedCarousel.reInit(options);
    }
  }, [isHydraPartialImageGallery, productFeaturedCarousel, styleId, windowWidth, zoomIsActive]);

  // ==== CAROUSEL EVENTS
  const onFeaturedSelect = useCallback(() => {
    // When the asset in the featured section is selected, the correspondent thumbnail is selected as well
    if (productFeaturedCarousel && productThumbnailsCarousel) {
      const selectedScrollSnap = productFeaturedCarousel.selectedScrollSnap();
      setSelectedAsset(selectedScrollSnap);
      productThumbnailsCarousel.scrollTo(selectedScrollSnap);

      !zoomIsActive &&
        track(() => [
          evImageSwipe,
          {
            asset: productAssets[selectedScrollSnap]
          }
        ]);
    }
  }, [productFeaturedCarousel, productThumbnailsCarousel, setSelectedAsset]);
  useEffect(() => {
    productFeaturedCarousel?.on('select', onFeaturedSelect);
    return function cleanup() {
      productFeaturedCarousel?.off('select', onFeaturedSelect);
    };
  }, [onFeaturedSelect, productFeaturedCarousel]);

  const handleFeaturedClick = (isSelected: boolean) => {
    setZoomIsActive(isSelected);
  };

  const getGalleryChildren = (asset: FeaturedImage | PDPFeaturedVideo, isSelected: boolean, isHydraPartialImageGallery?: boolean) =>
    'thumbnail' in asset ? (
      <ImageFeatured
        image={asset}
        isSelected={isSelected}
        zoomIsActive={zoomIsActive}
        showInstructions={showInstructions}
        setShowInstructions={setShowInstructions}
        prependedAltText={prependedAltText}
        imageHasBadge={imageHasBadge}
        renderDeselected={isHydraPartialImageGallery}
      />
    ) : (
      <VideoFeatured video={asset} isSelected={isSelected} />
    );

  return (
    <div id="productFeatured" className={css.productFeatured}>
      <div className={css.carousel} ref={productFeaturedRef}>
        <ul className={css.slides}>
          {productAssets.map((asset, index) => {
            const isSelected = selectedAsset === asset.index;
            const sizingChart = asset.angleType && asset.angleType === 'CHART';
            return (
              <li
                className={cn(css.slide, {
                  [css.slidePartialImage]: isHydraPartialImageGallery && 'thumbnail' in asset && !(isSelected && zoomIsActive) && !sizingChart
                })}
                key={index}
              >
                {!zoomIsActive ? (
                  <button
                    type="button"
                    aria-label={`${asset.alt} Zoom`}
                    aria-current={isSelected}
                    tabIndex={isSelected ? 0 : -1}
                    onClick={() => handleFeaturedClick(isSelected)}
                    data-media={asset.type}
                    data-test-id={testId(`${asset.type}Featured`)}
                    className={cn(css.slideFeatured, {
                      [css.zoomMode]: zoomIsActive
                    })}
                    {...getPDPTrackingPropsFormatted('Zoom-In', `${asset.type}-Click`)}
                  >
                    {getGalleryChildren(asset, isSelected, isHydraPartialImageGallery)}
                  </button>
                ) : (
                  getGalleryChildren(asset, isSelected, isHydraPartialImageGallery)
                )}
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
};

export default withErrorBoundary('ProductGalleryFeatured', ProductGalleryFeatured);
